<script setup>
import { ref } from 'vue'

const activeLogTab = ref('network')

const networkActivity = ref([
  {
    timestamp: '2024-03-20 10:26:00',
    type: 'PORT',
    status: 'OPEN',
    source: '443',
    destination: 'HTTPS',
    details: 'SSL/TLS enabled'
  },
  {
    timestamp: '2024-03-20 10:25:00',
    type: 'CONNECTION',
    status: 'ACTIVE',
    source: '10.0.0.5:54321',
    destination: '10.0.0.10:3306',
    details: 'MySQL connection'
  },
  {
    timestamp: '2024-03-20 10:24:00',
    type: 'PORT',
    status: 'CLOSED',
    source: '80',
    destination: 'HTTP',
    details: 'Port closed by policy'
  },
  {
    timestamp: '2024-03-20 10:23:00',
    type: 'CONNECTION',
    status: 'TERMINATED',
    source: '192.168.1.100:45678',
    destination: '10.0.0.20:27017',
    details: 'MongoDB connection terminated'
  }
])

const getTypeClass = (type) => {
  const classes = {
    PORT: 'bg-blue-100 text-blue-800',
    CONNECTION: 'bg-purple-100 text-purple-800',
    TRAFFIC: 'bg-green-100 text-green-800',
    ERROR: 'bg-red-100 text-red-800'
  }
  return classes[type] || 'bg-gray-100 text-gray-800'
}

const getStatusClass = (status) => {
  const classes = {
    OPEN: 'bg-green-100 text-green-800',
    ACTIVE: 'bg-blue-100 text-blue-800',
    CLOSED: 'bg-red-100 text-red-800',
    TERMINATED: 'bg-yellow-100 text-yellow-800'
  }
  return classes[status] || 'bg-gray-100 text-gray-800'
}

const switchLogTab = (tab) => {
  activeLogTab.value = tab
}
</script>

<template>
  <div class="p-4">
    <h1 class="text-2xl font-bold mb-6">System Logs</h1>
    
    <!-- Log Type Tabs -->
    <div class="bg-white rounded-lg shadow">
      <div class="p-4 border-b border-gray-200">
        <h3 class="text-lg font-semibold">System Monitoring & Logs</h3>
        
        <div class="mt-4 border-b border-gray-200">
          <nav class="-mb-px flex space-x-6">
            <button
              v-for="(tab, index) in [
                { id: 'system', icon: 'fa-server', label: 'System Logs' },
                { id: 'security', icon: 'fa-shield-alt', label: 'Security' },
                { id: 'network', icon: 'fa-network-wired', label: 'Network' },
                { id: 'performance', icon: 'fa-tachometer-alt', label: 'Performance' },
                { id: 'activity', icon: 'fa-history', label: 'User Activity' }
              ]"
              :key="index"
              @click="switchLogTab(tab.id)"
              :class="[
                'py-2 px-3 border-b-2 text-sm font-medium whitespace-nowrap',
                activeLogTab === tab.id
                  ? 'border-pink-500 text-pink-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              ]"
            >
              <i :class="['fas', tab.icon, 'mr-2']"></i>
              {{ tab.label }}
            </button>
          </nav>
        </div>
      </div>

      <!-- Network Logs Table -->
      <div v-if="activeLogTab === 'network'" class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Timestamp
              </th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Type
              </th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Source/Port
              </th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Destination/Service
              </th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Details
              </th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="log in networkActivity" :key="log.timestamp">
              <td class="px-6 py-4 whitespace-nowrap text-sm">{{ log.timestamp }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm">
                <span :class="['px-2 py-1 text-xs font-medium rounded-full', getTypeClass(log.type)]">
                  {{ log.type }}
                </span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <span :class="['px-2 py-1 text-xs font-medium rounded-full', getStatusClass(log.status)]">
                  {{ log.status }}
                </span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm font-mono">
                {{ log.source }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm">
                {{ log.destination }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ log.details }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Other tab contents here -->
      <div v-else-if="activeLogTab === 'system'" class="p-4">
        <div class="bg-gray-900 text-gray-100 p-4 rounded font-mono text-sm h-96 overflow-y-auto">
          <div class="mb-1">[2024-03-20 14:30:15] INFO: System startup completed</div>
          <div class="mb-1">[2024-03-20 14:30:16] INFO: Database connection established</div>
          <div class="mb-1 text-yellow-400">[2024-03-20 14:30:18] WARNING: High memory usage detected</div>
        </div>
      </div>
    </div>
  </div>
</template> 